/**
 * Math3D — main app shell (T-1.1.5 + T-1.2.1 + T-1.3.1).
 *
 * Layout (3 columns, T-1.3.1):
 *   - Left:   ShapePanel — drag source for cube (and future shapes).
 *   - Center: 3D scene canvas (MainCanvas → MainScene → Three.js) with
 *             a top Toolbar overlay that exposes "重置视角" (T-1.1.5).
 *   - Right:  sidebar placeholder (M3 will fill this with the
 *             six-direction control buttons and undo/redo).
 *
 * T-1.1.5 wires the toolbar by capturing the MainScene instance through
 * a callback ref (MainCanvas.onSceneMount). The toolbar's onResetView
 * button calls mainScene.resetView() to snap back to the PRD FR-101
 * isometric default. The reset handler is null-safe — clicking before
 * MainScene is mounted is a no-op (in practice impossible in this layout
 * since the Toolbar lives over the canvas, but the check costs nothing).
 *
 * T-1.2.1 adds a debug `data-testid="voxel-count"` placeholder above
 * the Toolbar so we can eyeball in the browser that the sceneStore
 * wires into the React tree. The element just renders `voxels.size`
 * via a Zustand selector — no command logic yet (that lands in
 * T-1.3.x / PlaceVoxel). The placeholder is intentionally minimal:
 * the store is the data plane, MainScene is the render plane, and
 * T-1.2.1 deliberately does NOT touch the existing MainScene ref
 * or onResetView wiring (Constitution §2.2 / T-1.1.5 hand-off note).
 *
 * T-1.3.1 inserts ShapePanel as the left column. ShapePanel is a pure
 * presentational component — it does NOT touch MainScene refs, does
 * NOT call sceneStore, and does NOT register any drop handlers. Drop
 * logic lands in T-1.3.3 (MainCanvas dragover/drop), command logic in
 * T-1.3.5 (PlaceVoxel). Constitution §2.3 — 不持有 MainScene ref.
 */
import { useCallback, useRef } from 'react';
import { MainCanvas } from '@/render/MainCanvas';
import type { MainScene } from '@/render/MainScene';
import { Toolbar } from '@/ui/Toolbar';
import { ShapePanel } from '@/ui/ShapePanel';
import { useSceneStore } from '@/state/sceneStore';
import './App.css';

function App(): JSX.Element {
  const mainSceneRef = useRef<MainScene | null>(null);

  const handleSceneMount = useCallback((scene: MainScene | null) => {
    mainSceneRef.current = scene;
  }, []);

  const handleResetView = useCallback(() => {
    mainSceneRef.current?.resetView();
  }, []);

  // T-1.2.1 debug placeholder — confirms sceneStore is wired into the
  // React tree. Replaced by a real voxel counter once PlaceVoxel lands
  // in T-1.3.x.
  const voxelCount = useSceneStore((s) => s.voxels.size);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '220px 1fr 320px',
        height: '100vh',
        width: '100vw',
        background: '#0a0a0a',
        color: '#e8e8e8',
        overflow: 'hidden',
      }}
    >
      <ShapePanel />
      <main
        style={{
          position: 'relative',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <MainCanvas onSceneMount={handleSceneMount} />
        <Toolbar onResetView={handleResetView} />
        <div
          data-testid="voxel-count"
          style={{
            position: 'absolute',
            bottom: '0.5rem',
            right: '0.75rem',
            padding: '0.25rem 0.5rem',
            fontSize: '0.75rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            color: '#888',
            background: 'rgba(17, 17, 17, 0.7)',
            border: '1px solid #222',
            borderRadius: '3px',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          voxels: {voxelCount}
        </div>
      </main>
      <aside
        style={{
          padding: '1.25rem',
          borderLeft: '1px solid #222',
          background: '#111',
          fontSize: '0.9rem',
          lineHeight: 1.5,
        }}
      >
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>观察控制</h2>
        <p style={{ margin: 0, color: '#888' }}>
          T-1.1.5 / M3 会接入六方向按钮、撤销/重做、立体图形选择器。
        </p>
      </aside>
    </div>
  );
}

export default App;
